import { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut, Trash2, RefreshCw, MailCheck, MailX } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/site/Seo";
import { Logo } from "../components/site/Logo";
import { api, formatApiError } from "../lib/api";

const ESTADOS = [["novo", "Novo"], ["em_contacto", "Em contacto"], ["proposta_enviada", "Proposta enviada"], ["fechado", "Fechado"], ["arquivado", "Arquivado"]];
const inputCls = "w-full rounded-xl border border-forest/15 bg-white px-4 py-3 outline-none focus:border-forest focus:ring-4 focus:ring-forest/10";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (ex) {
      setErr(formatApiError(ex, "Credenciais inválidas"));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="mx-auto mt-10 max-w-sm space-y-4 rounded-[1.75rem] border border-forest/10 bg-white p-8">
      <h1 className="font-display text-2xl font-semibold tracking-tight text-forest">Área reservada</h1>
      <div><label htmlFor="a-email" className="mb-2 block text-sm font-medium">E-mail</label><input id="a-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email-input" autoComplete="username" /></div>
      <div><label htmlFor="a-pass" className="mb-2 block text-sm font-medium">Palavra-passe</label><input id="a-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-password-input" autoComplete="current-password" /></div>
      {err && <p role="alert" className="text-sm text-red-700" data-testid="admin-login-error">{err}</p>}
      <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="admin-login-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}</button>
    </form>
  );
};

const Detail = ({ k, v }) => (v ? <p><span className="text-ink/60">{k}: </span>{String(v)}</p> : null);

const LeadRow = ({ lead, onStatus, onDelete }) => (
  <article data-testid={`admin-lead-${lead.id}`} className="rounded-2xl border border-forest/10 bg-white p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="flex items-center gap-2 font-display text-lg font-medium text-forest">
          {lead.nome}{lead.empresa ? ` · ${lead.empresa}` : ""}
          <span className={`rounded-full px-2.5 py-0.5 text-xs ${lead.tipo_cliente === "empresarial" ? "bg-forest text-white" : "bg-gold/25 text-[#6b4500]"}`}>{lead.tipo_cliente}</span>
        </p>
        <p className="mt-1 text-sm text-ink/70">{new Date(lead.criado_em).toLocaleString("pt-PT")} · origem: {lead.origem || "—"}</p>
      </div>
      <div className="flex items-center gap-2">
        <span title={lead.email_notificado ? "Notificação enviada" : "Notificação por e-mail falhou"}>{lead.email_notificado ? <MailCheck className="h-5 w-5 text-forest" /> : <MailX className="h-5 w-5 text-red-600" />}</span>
        <select aria-label="Estado do pedido" value={lead.estado} onChange={(e) => onStatus(lead.id, e.target.value)} data-testid={`admin-lead-status-${lead.id}`} className="rounded-lg border border-forest/15 px-3 py-2 text-sm">
          {ESTADOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button type="button" aria-label="Eliminar pedido" onClick={() => onDelete(lead.id)} data-testid={`admin-lead-delete-${lead.id}`} className="grid h-9 w-9 place-items-center rounded-lg border border-forest/15 text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
    <div className="mt-4 grid gap-1 text-sm sm:grid-cols-2">
      <p><a className="text-forest underline" href={`mailto:${lead.email}`}>{lead.email}</a> · <a className="text-forest underline" href={`tel:${lead.telefone.replace(/\s/g, "")}`}>{lead.telefone}</a></p>
      <Detail k="Local" v={`${lead.codigo_postal} ${lead.localidade}`} />
      <Detail k="Instalação" v={lead.tipo_instalacao} />
      <Detail k="Atividade" v={lead.atividade} />
      <Detail k="Habitação" v={lead.tipo_habitacao} />
      <Detail k="Telhado" v={lead.tipo_telhado} />
      <Detail k="Área" v={lead.area_disponivel} />
      <Detail k="Consumo" v={lead.consumo} />
      <Detail k="Baterias" v={lead.interesse_baterias ? "Interessado" : ""} />
    </div>
    {lead.info && <p className="mt-3 rounded-xl bg-paper p-3 text-sm text-ink/80">{lead.info}</p>}
  </article>
);

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false)).finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const onStatus = async (id, estado) => {
    try {
      await api.patch(`/admin/leads/${id}`, { estado });
      setLeads((l) => l.map((x) => (x.id === id ? { ...x, estado } : x)));
      toast.success("Estado atualizado");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Eliminar este pedido definitivamente?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((l) => l.filter((x) => x.id !== id));
      toast.success("Pedido eliminado");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const logout = async () => {
    await api.post("/auth/logout").catch(() => null);
    setUser(false);
  };

  return (
    <section data-testid="admin-page" className="min-h-screen bg-paper px-5 py-10">
      <Seo title="Área reservada | Mega Solar" description="Área reservada." path="/admin" noindex />
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between"><Logo />
          {user && <button type="button" onClick={logout} className="btn-outline !py-2" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Sair</button>}
        </div>
        {checking ? <Loader2 className="mx-auto mt-20 h-6 w-6 animate-spin text-forest" /> : !user ? <Login onLogin={setUser} /> : (
          <div className="mt-10">
            <div className="flex items-center justify-between">
              <h1 className="font-display text-3xl font-semibold tracking-tight text-forest">Pedidos de orçamento <span className="text-ink/50" data-testid="admin-leads-count">({leads.length})</span></h1>
              <button type="button" onClick={load} aria-label="Atualizar" className="grid h-10 w-10 place-items-center rounded-full border border-forest/15" data-testid="admin-refresh-button"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
            </div>
            <div className="mt-6 space-y-4">
              {leads.length === 0 && !loading && <p className="rounded-2xl border border-dashed border-forest/20 p-8 text-center text-ink/70" data-testid="admin-empty">Ainda não existem pedidos.</p>}
              {leads.map((l) => <LeadRow key={l.id} lead={l} onStatus={onStatus} onDelete={onDelete} />)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
